import { useMemo, type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useFlag } from '@rsos/flags';
import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { useIsEditingResourceRequest } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import AirAmbulancePageHeader from '../AirAumbulancePage/AirAmbulancePageHeader';
import AirAmbulanceEtlEditForm from '../etl/AirAmbulanceEtlEditForm';
import AirAmbulanceEtlStatus from '../etl/AirAmbulanceEtlStatus';
import useAirAmbulanceEtlEta from '../etl/hooks/useAirAmbulanceEtlEta';
import useAirAmbulanceEtlStatus from '../etl/hooks/useAirAmbulanceEtlStatus';
import AirAmbulanceStatus from '../midas/AirAmbulanceStatus';
import { useExistingRequest } from '../midas/hooks/useExistingRequest';
import useTentativeETA from '../midas/hooks/useTentativeETA';
import AirAmbulanceFormStandalone from './AirAmbulanceFormStandalone';
import { FORM_PAGES, FORM_HEADER, STATUS_HEADER } from './constants';

interface AirAmbulanceState {
  airAmbulance: {
    formPageToShow: string | null;
  };
}

interface EmergencyCallsState {
  emergencyCalls: {
    selectedCallerID: string | null;
    callQueue: Record<string, EmergencyCall>;
  };
}

interface CurrentHeaderState {
  text: string;
  subText?: string | null;
}

interface AirAmbulanceFormProps {
  fromMapCard?: boolean;
  hideBackButton?: boolean;
  noBorder?: boolean;
}

/**
 * Inner component for ETL-based Air Ambulance form.
 * Uses Redux airAmbulanceSlice for ETL state (tripId, transport).
 */
function AirAmbulanceFormEtlImpl({
  fromMapCard = false,
  hideBackButton = false,
  noBorder = false,
}: AirAmbulanceFormProps): JSX.Element {
  const { selectedCallerID, callQueue } = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls,
  );

  const isEditing = useIsEditingResourceRequest();

  // ETL status hook provides vehicle/provider/ETA info from ETL service
  const {
    transport,
    latestVehicle,
    latestProvider,
    brandTail,
    selectedRequestId,
  } = useAirAmbulanceEtlStatus();

  const hasLocationToRender = useMemo(
    () =>
      getMostRecentLoc(
        selectedCallerID && callQueue ? callQueue[selectedCallerID] : undefined,
      )
        ? true
        : false,
    [selectedCallerID, callQueue],
  );

  const eta = useAirAmbulanceEtlEta();

  const { currentHeader, currentPage } = useMemo((): {
    currentHeader: CurrentHeaderState;
    currentPage: ReactElement;
  } => {
    // For ETL, show edit form when we have a selectedRequestId and isEditing
    if (selectedRequestId && isEditing && transport) {
      return {
        currentHeader: {
          ...STATUS_HEADER,
          subText: [latestVehicle?.name, brandTail, latestProvider?.name]
            .filter(Boolean)
            .join(' - '),
        },
        currentPage: <AirAmbulanceEtlEditForm transport={transport} />,
      };
    }

    // For ETL, show status page when we have a selectedRequestId
    // TODO: we may need to add a "type" to these requests to support generic, non-air-ambulance requests in the future and filter it here.
    if (selectedRequestId) {
      return {
        currentHeader: {
          ...STATUS_HEADER,
          subText: [
            eta?.display,
            latestVehicle?.name,
            brandTail,
            latestProvider?.name,
          ]
            .filter(Boolean)
            .join(' - '),
        },
        currentPage: <AirAmbulanceEtlStatus />,
      };
    }

    return {
      currentHeader: {
        ...FORM_HEADER,
        subText: eta?.display,
      },
      currentPage: (
        <AirAmbulanceFormStandalone
          hasLocationToRender={hasLocationToRender}
          tentativeETA={eta}
          fromMapCard={fromMapCard}
        />
      ),
    };
  }, [
    hasLocationToRender,
    selectedRequestId,
    isEditing,
    transport,
    eta,
    brandTail,
    fromMapCard,
    latestProvider?.name,
    latestVehicle?.name,
  ]);

  return (
    <FullWidthContent backgroundColor="background" noBorder={noBorder}>
      <AirAmbulancePageHeader
        text={currentHeader?.text}
        subtext={currentHeader?.subText}
        hideBackButton={!selectedCallerID || hideBackButton}
      />
      {currentPage}
    </FullWidthContent>
  );
}

/**
 * Inner component for standard (Midas-based) Air Ambulance form.
 * Uses Redux for state management.
 * @deprecated Migrating to AirAmbulanceFormEtlImpl via the air_ambulance_etl_integrations feature flag.
 */
function AirAmbulanceFormMidasImpl({
  fromMapCard = false,
  hideBackButton = false,
  noBorder = false,
}: AirAmbulanceFormProps): JSX.Element {
  const { formPageToShow } = useSelector(
    (state: AirAmbulanceState) => state.airAmbulance,
  );
  const { selectedCallerID, callQueue } = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls,
  );
  const {
    existingRequest,
    existingRequestBody,
    latestVehicle,
    latestProvider,
  } = useExistingRequest();

  const hasLocationToRender = useMemo(
    () =>
      getMostRecentLoc(
        selectedCallerID && callQueue ? callQueue[selectedCallerID] : undefined,
      ),
    [selectedCallerID, callQueue],
  );
  const tentativeETA = useTentativeETA();

  const { currentHeader, currentPage } = useMemo((): {
    currentHeader: CurrentHeaderState;
    currentPage: ReactElement;
  } => {
    if (formPageToShow === FORM_PAGES.STATUS && existingRequest) {
      const brand_tail = existingRequestBody?.brand_tail;

      return {
        currentHeader: {
          ...STATUS_HEADER,
          subText: [latestVehicle?.name, brand_tail, latestProvider?.name]
            .filter(Boolean)
            .join(' - '),
        },
        currentPage: <AirAmbulanceStatus />,
      };
    }

    return {
      currentHeader: {
        ...FORM_HEADER,
        subText: tentativeETA?.display,
      },
      currentPage: (
        <AirAmbulanceFormStandalone
          hasLocationToRender={Boolean(hasLocationToRender)}
          tentativeETA={tentativeETA}
          fromMapCard={fromMapCard}
        />
      ),
    };
  }, [
    formPageToShow,
    hasLocationToRender,
    existingRequest,
    tentativeETA,
    existingRequestBody?.brand_tail,
    fromMapCard,
    latestProvider?.name,
    latestVehicle?.name,
  ]);

  return (
    <FullWidthContent backgroundColor="background" noBorder={noBorder}>
      <AirAmbulancePageHeader
        text={currentHeader?.text}
        subtext={currentHeader?.subText}
        hideBackButton={!selectedCallerID || hideBackButton}
      />
      {currentPage}
    </FullWidthContent>
  );
}

/**
 * Main Air Ambulance Form component.
 * Routes between ETL and Midas implementations based on feature flag.
 */
function AirAmbulanceForm({
  fromMapCard = false,
  hideBackButton = false,
  noBorder = false,
}: AirAmbulanceFormProps): JSX.Element {
  const isEtlIntegrationEnabled = useFlag('air_ambulance_etl_integrations');

  const Impl = isEtlIntegrationEnabled
    ? AirAmbulanceFormEtlImpl
    : AirAmbulanceFormMidasImpl;

  return (
    <Impl
      fromMapCard={fromMapCard}
      hideBackButton={hideBackButton}
      noBorder={noBorder}
    />
  );
}

export default AirAmbulanceForm;
